import React from 'react';
import { useApolloClient } from '@apollo/react-hooks';

import { getMerchantName } from '../merchants/merchants';
import { MerchantProductOffer } from './conversationMessages';
import { UserSavedMerchantProductOffer } from '../../api/graphql/queries/savedMerchantProductOffers';

export type MerchantIdToMerchantNameMap = {
    [merchantId: string]: string | undefined;
};

/**
 * This hook fetches the corresponding merchant names and stores them in a map for a list of merchant product offers
 */
export function useMerchantIdToMerchantNameMap({
    merchantProductOffers,
}: {
    merchantProductOffers: MerchantProductOffer[] | undefined;
}) {
    const apolloClient = useApolloClient();
    const [merchantIdToMerchantNameMap, setMerchantIdToMerchantNameMap] = React.useState<MerchantIdToMerchantNameMap>({});
    React.useEffect(() => {
        if (!merchantProductOffers?.length) return;
        const merchantIdsToFetch = merchantProductOffers
            .map((offer) => offer.merchantId)
            .filter((merchantId): merchantId is string => !!merchantId && !merchantIdToMerchantNameMap[merchantId]);
        if (merchantIdsToFetch.length === 0) return;
        const fetchMerchantNames = async () => {
            // We use Promise.allSettled to ensure that individual failures don't prevent all the other merchant names from being fetched
            const merchantNames = await Promise.allSettled(
                merchantIdsToFetch.map((merchantId) => getMerchantName(apolloClient, merchantId))
            );
            const newMerchantIdToMerchantNameMap = merchantNames.reduce((newMap: MerchantIdToMerchantNameMap, result) => {
                if (result.status === 'fulfilled') newMap[result.value.merchantId] = result.value.merchantName;
                return newMap;
            }, {});
            setMerchantIdToMerchantNameMap((previousMap) => ({
                ...previousMap,
                ...newMerchantIdToMerchantNameMap,
            }));
        };
        fetchMerchantNames();
    }, [merchantProductOffers]);
    return merchantIdToMerchantNameMap;
}

export function useHandleScroll({
    listRef,
    listContent,
    shouldAutoScrollToBottom,
    scrollBehavior = 'smooth',
    onScrollCallback,
}: {
    listRef: React.MutableRefObject<HTMLDivElement | null>;
    listContent: unknown;
    /** If this is true, the list will automatically scroll to the bottom when new content is added to make it visible */
    shouldAutoScrollToBottom?: boolean;
    scrollBehavior?: ScrollBehavior;
    onScrollCallback?: (isAtBottom: boolean) => void;
}) {
    const [isAtBottom, setIsAtBottom] = React.useState(true);
    const previousScrollTopRef = React.useRef(0);
    const shouldDisableAutoScrollingRef = React.useRef(false);
    const onScroll = () => {
        const container = listRef.current;
        if (!container) return;
        const currentScrollTop = container.scrollTop;
        // If the user has scrolled up we disable the auto-scrolling when new content is added to let the user read the previous content
        if (currentScrollTop < previousScrollTopRef.current) shouldDisableAutoScrollingRef.current = true;
        previousScrollTopRef.current = currentScrollTop;
        const isAtBottom = Math.round(container.scrollTop + container.clientHeight) >= Math.round(container.scrollHeight);
        if (isAtBottom) {
            // We reactivate the auto-scrolling once the user scrolls back to the bottom
            shouldDisableAutoScrollingRef.current = false;
            setIsAtBottom(true);
        } else setIsAtBottom(false);
        onScrollCallback?.(isAtBottom);
    };
    React.useEffect(() => {
        const container = listRef.current;
        if (!container) return;
        if (shouldAutoScrollToBottom && !shouldDisableAutoScrollingRef.current) {
            setIsAtBottom(true);
            scrollToBottom(listRef, scrollBehavior);
            return;
        }
        if (!shouldAutoScrollToBottom) {
            const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight;
            setIsAtBottom(isAtBottom);
        }
    }, [listContent]);
    return { isAtBottom, setIsAtBottom, onScroll };
}

export function scrollToBottom(listRef: React.MutableRefObject<HTMLDivElement | null>, scrollBehavior: ScrollBehavior) {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: scrollBehavior });
}

export function mapSavedMerchantProductOfferToMerchantProductOffer(
    savedOffer: UserSavedMerchantProductOffer
): MerchantProductOffer {
    return {
        merchantId: savedOffer.merchantId,
        merchantProductOfferId: savedOffer.merchantProductOfferId,
        originalTitle: savedOffer.originalTitle,
        processedWebpageUrl: savedOffer.processedWebpageUrl,
        mainImageUrls: {
            defaultSize: savedOffer.mainImageUrls.defaultSize,
        },
        priceInformation: {
            displayPriceAmount: {
                currency: savedOffer.displayPriceAmount.currency,
                valueInCents: savedOffer.displayPriceAmount.valueInCents,
            },
        },
        contextualInformation: {
            originalMerchantName: savedOffer.merchantId,
        },
    };
}
